export const noop = () => {};
import { StrategyBuilderFunction, EMPTY_STRATEGY } from './types';
import { addShortPut, addLongPut } from './addOption';
import {
  getDefaultExpiration,
  findClosestListedStrike,
  getStrikesAtExpiration,
  expirationToDate,
  getPremium,
  getStrategyLegStrikeDirection,
  getStrikeDistance,
} from './utils';

export const diagonalPutSpread: StrategyBuilderFunction = ({
  chain,
  spot,
  expirationDate,
  expirationKey,
  strike,
  strikeDirection,
  contractMultiplier = 1,
}) => {
  if (!chain || !chain.length) {
    return EMPTY_STRATEGY;
  }

  // Determine the expiration dates for the short-term and long-term options
  const strikesAtExpiration = expirationKey
    ? getStrikesAtExpiration(chain, expirationKey)
    : getDefaultExpiration(chain);

  if (
    !strikesAtExpiration ||
    !strikesAtExpiration.e ||
    !strikesAtExpiration.ey ||
    !strikesAtExpiration.em ||
    !strikesAtExpiration.ed
  ) {
    return EMPTY_STRATEGY;
  }

  const longTermStrikesAtExpiration = getDefaultExpiration(chain, 2); // Next available expiration

  if (
    !longTermStrikesAtExpiration ||
    !longTermStrikesAtExpiration.e ||
    !longTermStrikesAtExpiration.ey ||
    !longTermStrikesAtExpiration.em ||
    !longTermStrikesAtExpiration.ed
  ) {
    return EMPTY_STRATEGY;
  }

  // Assuming chain is sorted by expiration date
  const shortTermExpiration =
    expirationDate ||
    expirationToDate(strikesAtExpiration.ey, strikesAtExpiration.em, strikesAtExpiration.ed);

  const longTermExpiration = expirationToDate(
    longTermStrikesAtExpiration.ey,
    longTermStrikesAtExpiration.em,
    longTermStrikesAtExpiration.ed,
  );

  const expirationKeyForOptions = expirationKey || strikesAtExpiration.e;
  // Each leg's strike comes from the expiry it trades in: the long leg's back month can list a
  // different strike grid, and an unlisted strike would be priced at a fabricated $0.
  const lowerStrikePrice = findClosestListedStrike(
    [strikesAtExpiration],
    strike ?? getStrikeDistance({ price: spot, unit: -2 }),
    'p',
  );
  const higherStrikePrice = findClosestListedStrike(
    [longTermStrikesAtExpiration],
    strike ?? getStrikeDistance({ price: spot, unit: 2 }),
    'p',
  );

  if (lowerStrikePrice === -1 || higherStrikePrice === -1) {
    return EMPTY_STRATEGY;
  }

  // Build the short-term call option position (sold)
  const shortTermOption = addShortPut({
    strike: lowerStrikePrice,
    premium: getPremium({
      ask: strikesAtExpiration?.c?.[lowerStrikePrice]?.p?.a || 0,
      bid: strikesAtExpiration?.c?.[lowerStrikePrice]?.p?.b || 0,
      strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'short'),
    }),
    expirationDate: shortTermExpiration,
    expirationKey: expirationKeyForOptions,
    numberOfContracts: 1 * contractMultiplier,
  });

  // Build the long-term call option position (bought)
  const longTermOption = addLongPut({
    strike: higherStrikePrice,
    premium: getPremium({
      ask: longTermStrikesAtExpiration?.c?.[higherStrikePrice]?.p?.a || 0,
      bid: longTermStrikesAtExpiration?.c?.[higherStrikePrice]?.p?.b || 0,
      strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'long'),
    }),
    expirationDate: longTermExpiration,
    expirationKey: longTermStrikesAtExpiration.e,
    numberOfContracts: 1 * contractMultiplier,
  });

  return {
    optionPositions: [shortTermOption, longTermOption],
    stockPositions: [],
  };
};
